<!--
Molecule for displaying a session for a new lead in a grid.
If clicked, go to the new session view to resume the session.
-->
<template>
	<router-link :to="{ name: 'newSession', query: { sessionId: session.id } }">
		<div class="ml-3 flex flex-row gap-3">
			<!-- Status -->
			<AppointmentCloudSyncIcon :current-state="currentSyncState" />

			<!-- Name -->
			<div class="flex items-center gap-2 text-sm font-bold">
				<!-- If plan loaded -->
				<div
					v-if="!isLoadingPlan && planNames.length"
					class="text-sky-700"
				>
					{{ planNames[0]?.firstName }} {{ planNames[0]?.lastName }}
				</div>

				<!-- Skeleton loader while plan is loading -->
				<div
					v-else-if="isLoadingPlan"
					class="animate-pulse"
				>
					<div class="h-4 w-32 rounded-sm bg-slate-200"></div>
				</div>

				<!-- If no plan -->
				<div
					v-else
					class="text-secondary-400 italic"
				>
					{{ t('upcomingAppointments.noPlans') }}
				</div>
			</div>
		</div>

		<!-- Time -->
		<div>
			{{ startAt }}
		</div>

		<!--Funeral Home Name-->
		<div v-if="funeralHomeName">
			{{ funeralHomeName }}
		</div>
		<div
			v-else
			class="text-secondary-400 italic"
		>
			{{ t('upcomingAppointments.noFuneralHome') }}
		</div>

		<!--Lead Source-->
		<div>{{ t('upcomingAppointments.newLead') }}</div>

		<!-- Result button -->
		<div
			data-test-id="result-button"
			class="flex items-center justify-self-end"
		>
			<a
				:href="resultHref"
				target="_blank"
				@click.stop="handleResultClick"
				v-if="!isResulted"
			>
				<ButtonSmall>
					{{ t('upcomingAppointments.resultNewLead') }}
				</ButtonSmall>
			</a>

			<!-- If the user has added the lead to the webportal show static text saying so insted of a button -->
			<span
				v-else
				class="text-sm font-medium text-gray-400"
			>
				{{ t('upcomingAppointments.resultedNewLead') }}
			</span>
			<PrintOptions :session="session" />
		</div>
	</router-link>
</template>

<script setup lang="ts">
import type { Session } from '@/classes/Session'
import ButtonSmall from '@/components/forms/ButtonSmall.vue'
import AppointmentCloudSyncIcon from '@/components/home/AppointmentCloudSyncIcon.vue'
import PrintOptions from '@/components/home/PrintOptions.vue'
import { useCloudSyncStatusCalculation } from '@/mixins/cloudSyncStatusCalculation'
import { makeWebPortalUrl } from '@/services/webPortal'
import { useCrmStore } from '@/stores/crm'
import { usePlanStore } from '@/stores/plan'
import { useSessionStore } from '@/stores/session'
import { captureException } from '@sentry/vue'
import { DateTime } from 'luxon'
import { computed, onMounted, ref, type PropType } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const isLoadingPlan = ref(true)

const sessionStore = useSessionStore()
const plansStore = usePlanStore()
const crm = useCrmStore()

const props = defineProps({
	session: {
		type: Object as PropType<Session>,
		required: true,
	},
})

const planNames = ref<{ firstName: string; lastName: string }[]>([])

// Load plans, store names, and set isLoadingPlan to false afterwards
onMounted(async () => {
	try {
		if (props.session.planIds?.length) {
			const plans = await plansStore.loadPlans(props.session.planIds)
			planNames.value = plans.map((p) => ({
				firstName: p.firstName ?? '',
				lastName: p.lastName ?? '',
			}))
		}
	} catch (error) {
		captureException(error)
		console.error('Failed to load plan:', error)
	} finally {
		isLoadingPlan.value = false
	}
})

// Check if the session has been 'resulted'
const isResulted = computed(() => !!props.session.resultedAt)

// Set session resultedAt to current timestamp, save the session, and emit 'resulted'
function handleResultClick() {
	props.session.resultedAt = new Date().toISOString()
	sessionStore.saveSession(props.session)
}

/** The human-readable start time. Includes "AM" if it starts in the morning and ends in the evening. */
const startAt = computed(() => {
	if (props.session.startAt === null) return ''

	// Format like 1:00 PM
	let time = DateTime.fromISO(props.session.startAt).toLocaleString(
		DateTime.TIME_SIMPLE
	)

	return time
})

const funeralHomeName = computed(() =>
	props.session.FuneralHomeLocationId
		? crm.findFuneralHome(props.session.FuneralHomeLocationId)?.shortName
		: undefined
)

/** Get the cloudSync State */
const { currentSyncState } = useCloudSyncStatusCalculation(props.session)

/** URL for resulting the session */
const resultHref = computed(() => {
	return makeWebPortalUrl(`add-lead`)
})
</script>
